// Print a Profit & Loss statement on A4 — proper accounting format.
// Data sourced from /api/reports/profit-loss (server-side single source of truth).
import { API_BASE } from "@/lib/api";

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const escape = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
};

/**
 * pl: { revenue, cogs, gross_profit, expenses, net_profit, gross_margin_pct, net_margin_pct }
 * period: { start, end, label }
 * expensesBreakdown: optional array [{category, total}] to render under operating expenses
 */
export function printPL({ pl, period, expensesBreakdown = [], template, lang = "ar" }) {
  const isAR = lang === "ar";
  const dir = isAR ? "rtl" : "ltr";
  const t = (en, ar) => (isAR ? ar : en);
  const primary = template?.primary_color || "#D4AF64";

  const rev = Number(pl.revenue || 0);
  const cogs = Number(pl.cogs || 0);
  const gp = Number(pl.gross_profit || 0);
  const opex = Number(pl.expenses || 0);
  const net = Number(pl.net_profit ?? pl.profit ?? 0);
  const gm = Number(pl.gross_margin_pct || 0);
  const nm = Number(pl.net_margin_pct ?? pl.margin_pct ?? 0);
  const netClass = net >= 0 ? "pos" : "neg";

  const expRows = (expensesBreakdown || []).map((row, i) => `
    <tr class="sub">
      <td class="indent">${i + 1}.${i + 1}</td>
      <td class="indent">${escape(row.category || "—")}</td>
      <td class="num">(${fmt(row.total)})</td>
    </tr>
  `).join("");

  // If no breakdown supplied, just render a single aggregated line.
  const expBlock = expRows || `
    <tr class="sub">
      <td class="indent">2.1</td>
      <td class="indent">${t("Operating expenses", "مصروفات تشغيلية")}</td>
      <td class="num">(${fmt(opex)})</td>
    </tr>
  `;

  const html = `
<!DOCTYPE html>
<html lang="${isAR ? "ar" : "en"}" dir="${dir}">
<head>
<meta charset="UTF-8" />
<title>${t("Profit & Loss", "قائمة الأرباح والخسائر")}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: ${isAR ? "'IBM Plex Sans Arabic', sans-serif" : "'Inter', -apple-system, sans-serif"};
    color: #111; background: #fff; font-size: 11pt;
  }
  .page { width: 100%; max-width: 182mm; margin: 0 auto; padding: 8mm 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 12px; border-bottom: 2px solid ${primary}; }
  .brand { display: flex; gap: 12px; align-items: center; }
  .logo { width: 70px; height: 70px; object-fit: contain; }
  .biz h1 { margin: 0 0 4px 0; font-size: 20pt; color: ${primary}; }
  .biz .meta { font-size: 9pt; color: #555; line-height: 1.5; }
  .doc-info { text-align: ${isAR ? "left" : "right"}; }
  .doc-info .doc-title { font-size: 16pt; color: ${primary}; font-weight: 700; margin-bottom: 6px; }
  .doc-info .row { font-size: 10pt; color: #333; line-height: 1.6; }
  .doc-info .label { color: #777; margin-${isAR ? "left" : "right"}: 6px; }

  .pl-title { margin-top: 22px; text-align: center; }
  .pl-title h2 { margin: 0; font-size: 18pt; color: ${primary}; font-weight: 700; letter-spacing: 0.5px; }
  .pl-title .period { margin-top: 4px; font-size: 11pt; color: #555; }

  table.pl { width: 100%; border-collapse: collapse; margin-top: 20px; }
  table.pl th { background: ${primary}; color: #fff; padding: 9px 12px; font-size: 10pt; text-align: ${isAR ? "right" : "left"}; font-weight: 600; }
  table.pl th.num { text-align: ${isAR ? "left" : "right"}; }
  table.pl td { padding: 10px 12px; font-size: 11.5pt; border-bottom: 1px solid #eee; }
  table.pl td.num { text-align: ${isAR ? "left" : "right"}; font-variant-numeric: tabular-nums; white-space: nowrap; }
  table.pl tr.sub td { background: #fbfaf7; font-size: 10.5pt; color: #555; padding: 7px 12px; }
  table.pl tr.sub td.indent { padding-${isAR ? "right" : "left"}: 28px; }
  table.pl tr.total td { font-weight: 700; background: #f5efe1; border-top: 1.5px solid ${primary}; }
  table.pl tr.total td.num { color: ${primary}; }
  table.pl tr.grand td { font-weight: 700; background: ${primary}; color: #fff; border-top: 2px solid #000; }
  table.pl tr.grand td.num { color: #fff; font-size: 14pt; }
  table.pl tr.grand.neg td { background: #c2410c; }
  .pos { color: #16a34a; }
  .neg { color: #dc2626; }

  .summary { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; margin-top: 22px; }
  .summary .card { border: 1px solid #ddd; border-radius: 6px; padding: 12px 14px; background: #fafafa; text-align: center; }
  .summary .card .lbl { font-size: 9pt; color: #777; text-transform: uppercase; letter-spacing: .5px; }
  .summary .card .v { font-size: 16pt; font-weight: 700; margin-top: 6px; color: ${primary}; }
  .summary .card.gp .v { color: #16a34a; }
  .summary .card.gp.neg .v { color: #dc2626; }

  .formula-note { margin-top: 18px; padding: 10px 14px; background: #fafafa; border: 1px dashed #ccc; border-radius: 4px; font-size: 9.5pt; color: #666; line-height: 1.7; }
  .formula-note b { color: ${primary}; }

  .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 36px; }
  .sig-box { border-top: 1px solid #888; padding-top: 6px; font-size: 10pt; color: #444; text-align: center; }

  .footer { margin-top: 24px; padding-top: 14px; border-top: 1px dashed #aaa; font-size: 9pt; color: #888; text-align: center; }

  .actions { margin-top: 12px; text-align: center; padding-top: 14px; }
  .actions button { background: ${primary}; color: #fff; border: 0; padding: 10px 20px; border-radius: 6px; cursor: pointer; font-size: 11pt; }

  @media print {
    .actions { display: none; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="brand">
        ${template?.logo_url ? `<img class="logo" src="${escape(resolveImg(template.logo_url))}" alt="" />` : ""}
        <div class="biz">
          <h1>${escape(isAR ? template?.business_name_ar : template?.business_name_en) || ""}</h1>
          <div class="meta">
            ${template?.phone ? `<div>${t("Phone", "هاتف")}: ${escape(template.phone)}</div>` : ""}
            ${template?.email ? `<div>${escape(template.email)}</div>` : ""}
            ${template?.tax_number ? `<div>${t("VAT", "الرقم الضريبي")}: ${escape(template.tax_number)}</div>` : ""}
            ${template?.cr_number ? `<div>${t("CR", "س.ت")}: ${escape(template.cr_number)}</div>` : ""}
          </div>
        </div>
      </div>
      <div class="doc-info">
        <div class="doc-title">${t("Financial Statement", "بيان مالي")}</div>
        <div class="row"><span class="label">${t("Generated", "تاريخ الإصدار")}:</span> ${new Date().toLocaleString(isAR ? "ar-EG" : "en-US")}</div>
      </div>
    </div>

    <div class="pl-title">
      <h2>${t("Profit & Loss Statement", "قائمة الأرباح والخسائر")}</h2>
      <div class="period">${t("For the period", "عن الفترة")} ${escape(period.start)} ${isAR ? "إلى" : "to"} ${escape(period.end)}${period.label ? ` · ${escape(period.label)}` : ""}</div>
    </div>

    <table class="pl">
      <thead>
        <tr>
          <th style="width:10%;">#</th>
          <th>${t("Description", "البيان")}</th>
          <th class="num" style="width:25%;">${t("Amount (QAR)", "المبلغ (ر.ق)")}</th>
        </tr>
      </thead>
      <tbody>
        <tr class="total">
          <td>1</td>
          <td>${t("Revenue (Sales)", "الإيرادات (المبيعات)")}</td>
          <td class="num pos">${fmt(rev)}</td>
        </tr>
        <tr class="total">
          <td>2</td>
          <td>${t("Cost of Goods Sold (COGS)", "تكلفة البضاعة المباعة")}</td>
          <td class="num neg">(${fmt(cogs)})</td>
        </tr>
        <tr class="grand ${gp >= 0 ? "" : "neg"}">
          <td>=</td>
          <td>${t("Gross Profit", "إجمالي الربح")} <span style="opacity:.8;font-weight:400;">(${gm.toFixed(1)}%)</span></td>
          <td class="num">${fmt(gp)}</td>
        </tr>
        <tr><td colspan="3" style="border:0;height:6px;background:#fff;"></td></tr>
        <tr class="total">
          <td>3</td>
          <td>${t("Operating Expenses", "المصروفات التشغيلية")}</td>
          <td class="num neg">(${fmt(opex)})</td>
        </tr>
        ${expBlock}
        <tr><td colspan="3" style="border:0;height:6px;background:#fff;"></td></tr>
        <tr class="grand ${net >= 0 ? "" : "neg"}">
          <td>=</td>
          <td>${t(net >= 0 ? "Net Profit" : "Net Loss", net >= 0 ? "صافي الربح" : "صافي الخسارة")} <span style="opacity:.8;font-weight:400;">(${nm.toFixed(1)}%)</span></td>
          <td class="num">${fmt(net)}</td>
        </tr>
      </tbody>
    </table>

    <div class="summary">
      <div class="card">
        <div class="lbl">${t("Revenue", "الإيرادات")}</div>
        <div class="v">${fmt(rev)}</div>
      </div>
      <div class="card">
        <div class="lbl">${t("COGS", "ت. بضاعة")}</div>
        <div class="v">${fmt(cogs)}</div>
      </div>
      <div class="card gp ${gp >= 0 ? "" : "neg"}">
        <div class="lbl">${t("Gross Margin", "هامش إجمالي")}</div>
        <div class="v">${gm.toFixed(1)}%</div>
      </div>
      <div class="card gp ${net >= 0 ? "" : "neg"}">
        <div class="lbl">${t("Net Margin", "هامش صافي")}</div>
        <div class="v">${nm.toFixed(1)}%</div>
      </div>
    </div>

    <div class="formula-note">
      <div><b>${t("Formulas applied", "المعادلات المُطبقة")}:</b></div>
      <div>• ${t("Gross Profit = Revenue − COGS", "إجمالي الربح = الإيرادات − تكلفة البضاعة المباعة")}</div>
      <div>• ${t("Net Profit = Gross Profit − Operating Expenses", "صافي الربح = إجمالي الربح − المصروفات التشغيلية")}</div>
      <div>• ${t("COGS is recognized per invoice item at the product's recorded cost (services have no COGS).", "تكلفة البضاعة المباعة تُحتسب لكل بند فاتورة بتكلفة المنتج المسجلة (الخدمات لا تحمل تكلفة بضاعة).")}</div>
    </div>

    <div class="sig-grid">
      <div class="sig-box">${t("Prepared by — Accountant", "أعدّها — المحاسب")}</div>
      <div class="sig-box">${t("Approved by — Management", "اعتمدها — الإدارة")}</div>
    </div>

    <div class="footer">
      ${t("This is an internal financial statement. All figures are in QAR and exclude any cancelled/voided invoices.", "هذا بيان مالي داخلي. جميع المبالغ بالريال القطري وتستثني الفواتير الملغاة.")}
    </div>

    <div class="actions">
      <button onclick="window.print()">${t("Print", "طباعة")}</button>
    </div>
  </div>
  <script>setTimeout(function(){ window.print(); }, 400);</script>
</body>
</html>
  `;

  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) return;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  w.location.href = url;
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } }, 60_000);
}
